import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Icon, Input, Label, Modal, ModalProps, Step } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import useSwitchTouchScreenMutation from 'app/queries/useSwitchTouchScreenMutation';

type MonitorCalibrationModalProps = {
  onSuccess: ({ width, height }: { width: number; height: number }) => void;
  onCancel: () => void;
  initialLineLength?: number;
} & React.ComponentProps<typeof Modal>;

const TARGET_CM = 10; // Target length in cm for calibration

function drawLine(canvas: HTMLCanvasElement, lineLength: number) {
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'black';
    ctx.fillRect(0, canvas.height / 2 - 1, lineLength, 2);
    ctx.fillRect(0, canvas.height / 2 - 3, 1, 6);
    ctx.fillRect(lineLength, canvas.height / 2 - 3, 1, 6);
  }
}

const MonitorCalibrationModal: React.FC<MonitorCalibrationModalProps> = ({
  onSuccess,
  onCancel,
  initialLineLength,
  ...modalProps
}) => {
  const [step, setStep] = useState(1);
  const [pixelCountFor10cm, setPixelCountFor10cm] = useState<number | null>(null);
  // Assume 96 DPI
  const [lineWidth, setLineWidth] = useState(initialLineLength || 380); // Initial line width
  const [measuredMm, setMeasuredMm] = useState<number | null>(null);
  const [testMmLength, setTestMmLength] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isTouchScreenDisabled, setIsTouchScreenDisabled] = useState<boolean | undefined>(
    undefined
  );
  const { isPending: isTouchScreenSwitchPending, mutateAsync: switchTouchScreenAsync } =
    useSwitchTouchScreenMutation() ?? {};
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const testCanvasRef = useRef<HTMLCanvasElement>(null);

  const onClose = (event: React.MouseEvent<HTMLElement>, data: ModalProps) => {
    modalProps?.onClose?.(event, data);
    setIsTouchScreenDisabled(undefined);
  };

  const preventWheelScrollOnRef = useCallback((ref: HTMLDivElement) => {
    ref?.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
  }, []);

  const drawCalibrationLine = useCallback(() => {
    if (step !== 1 || !canvasRef.current) return;

    const canvas = canvasRef.current;
    drawLine(canvas, lineWidth);
  }, [step, lineWidth]);

  const drawTestLine = useCallback(() => {
    if (!(step === 2 && testCanvasRef.current && pixelCountFor10cm)) return;

    const canvas = testCanvasRef.current;
    // Minimum 50mm max 100mm, finite mm value
    const randomMmValue = Math.floor(Math.random() * 50 + 50);
    const pixelsPerMm = pixelCountFor10cm / (TARGET_CM * 10); // Convert to pixels
    const randomLength = randomMmValue * pixelsPerMm; // Convert to pixels

    drawLine(canvas, randomLength);
    setTestMmLength(randomMmValue); // Set calculated mm value
    setMeasuredMm(null);
  }, [step, pixelCountFor10cm]);

  const setCanvasRef = useCallback(
    (ref: HTMLCanvasElement | null) => {
      if (ref) {
        canvasRef.current = ref;
        canvasRef.current.width = ref.parentElement.clientWidth;
        drawCalibrationLine();
      }
    },
    [drawCalibrationLine]
  );

  const setTestCanvasRef = useCallback(
    (ref: HTMLCanvasElement | null) => {
      if (ref) {
        testCanvasRef.current = ref;
        testCanvasRef.current.width = ref.parentElement.clientWidth;
        drawTestLine();
      }
    },
    [drawTestLine]
  );

  const handleWheel = (event: React.WheelEvent) => {
    // We need extra precision so we move width 1px by 1 px to give the most fine grainde precision.
    setLineWidth((prevWidth) => Math.max(50, prevWidth + (event.deltaY > 0 ? 1 : -1)));
  };

  const handleConfirmCalibration = () => {
    setPixelCountFor10cm(lineWidth);

    setStep(2);
  };

  const handleRetryCalibration = () => {
    setStep(1);
    setMeasuredMm(null);
    setTestMmLength(null);
  };

  const handleValidateMeasurement = () => {
    if (measuredMm === null || testMmLength === null) return;

    const tolerance = 1; // 1mm tolerance
    if (Math.abs(measuredMm - testMmLength) <= tolerance) {
      const { width: screenWidth, height: screenHeight, orientation } = window.screen;
      const isRotated = orientation.angle % 180 !== 0;
      const realScreenWidth = isRotated ? screenHeight : screenWidth;
      const realScreenHeight = isRotated ? screenWidth : screenHeight;
      const pixelsPerMm = pixelCountFor10cm / (TARGET_CM * 10);
      const screenWidthMm = realScreenWidth / pixelsPerMm;
      const screenHeightMm = realScreenHeight / pixelsPerMm;
      onSuccess({ width: screenWidthMm, height: screenHeightMm });
    } else {
      // Fail, retry with a new line
      if (pixelCountFor10cm) {
        drawTestLine();
        setIsError(true);
        setTimeout(() => {
          setIsError(false);
        }, 3000); // Reset error state after 5 seconds
      } else {
        setStep(1);
      }
    }
  };

  useEffect(() => {
    if (!isTouchScreenDisabled) {
      return undefined;
    }
    if (isTouchScreenDisabled) {
      return () => {
        switchTouchScreenAsync?.({ enabled: true });
      };
    }
  }, [isTouchScreenDisabled, switchTouchScreenAsync]);

  return (
    <Modal {...modalProps} onClose={onClose}>
      <Modal.Header>
        <FormattedMessage id="monitorCalibrationModal.header" />
      </Modal.Header>
      <Modal.Content>
        <Step.Group>
          <Step active={step === 1} completed={step > 1}>
            <Icon name="cogs" />
            <Step.Content>
              <Step.Title>
                <FormattedMessage id="monitorCalibrationModal.calibrationStepTitle" />
              </Step.Title>
              {step === 1 && (
                <Step.Description>
                  <FormattedMessage id="monitorCalibrationModal.calibrationStepDescription" />
                </Step.Description>
              )}
            </Step.Content>
          </Step>

          <Step active={step === 2} disabled={step < 2}>
            <Icon name="clipboard check" />
            <Step.Content>
              <Step.Title>
                <FormattedMessage id="monitorCalibrationModal.validationStepTitle" />
              </Step.Title>
              {step === 2 && (
                <Step.Description>
                  <FormattedMessage id="monitorCalibrationModal.validationStepDescription" />
                </Step.Description>
              )}
            </Step.Content>
          </Step>
        </Step.Group>

        <div className="monitor-calibration" onWheel={handleWheel} ref={preventWheelScrollOnRef}>
          {step === 1 && (
            <>
              <p>
                <FormattedMessage id="monitorCalibrationModal.calibrationInstructions" />
              </p>
              <p>
                <FormattedMessage id="monitorCalibrationModal.adjustLineLength" />
              </p>
              <div
                style={{
                  minWidth: '50vw',
                  width: '80%',
                  height: '50px',
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <canvas height={50} ref={setCanvasRef} />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p>
                <FormattedMessage id="monitorCalibrationModal.measurementInstructions" />
              </p>
              <div
                style={{
                  minWidth: '50vw',
                  width: '80%',
                  height: '50px',
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <canvas height={50} ref={setTestCanvasRef} />
              </div>
              <div>
                <Input
                  type="number"
                  id="measuredMm"
                  min="50"
                  max="150"
                  step="1"
                  placeholder="mm"
                  value={measuredMm === null ? '' : measuredMm}
                  onChange={(e, { value }) => {
                    setMeasuredMm(Number(value));
                    setIsError(false);
                  }}
                  label
                  labelPosition="right"
                >
                  <Label basic>
                    <FormattedMessage id="monitorCalibrationModal.lengthLabel" />
                  </Label>
                  <input />
                  <Label>mm</Label>
                </Input>
                <div
                  style={{
                    opacity: isError ? '1' : '0',
                    color: '#9f3a38',
                    fontWeight: 700,
                    marginLeft: '1em',
                    fontSize: '0.8em',
                  }}
                >
                  <FormattedMessage id="monitorCalibrationModal.measurementError" />
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        {step === 1 && (
          <>
            <Button primary onClick={handleConfirmCalibration}>
              <FormattedMessage id="monitorCalibrationModal.confirmCalibration" />
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button primary onClick={handleValidateMeasurement}>
              <FormattedMessage id="monitorCalibrationModal.testMeasurement" />
            </Button>
            <Button color="red" onClick={handleRetryCalibration}>
              <FormattedMessage id="monitorCalibrationModal.retryCalibration" />
            </Button>
          </>
        )}
        {switchTouchScreenAsync && (
          <Button
            secondary
            onClick={async () => {
              if (isTouchScreenDisabled) {
                setIsTouchScreenDisabled(false);
              } else {
                switchTouchScreenAsync({ enabled: false }).finally(() =>
                  setIsTouchScreenDisabled(true)
                );
              }
            }}
            loading={isTouchScreenSwitchPending}
            disabled={isTouchScreenSwitchPending}
          >
            {isTouchScreenDisabled ? (
              <FormattedMessage id="monitorCalibrationModal.enableTouchScreen" />
            ) : (
              <FormattedMessage id="monitorCalibrationModal.disableTouchScreen" />
            )}
          </Button>
        )}
        <Button secondary onClick={onCancel}>
          <FormattedMessage id="general.cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default MonitorCalibrationModal;
